import RubberTable from '@app/components/rubber-table';
import { TableColumn } from '@app/components/rubber-table/table.types';
import { OptionItem } from '@app/components/ui/select/select.type';
import useTranslation from '@app/hooks/use-translation';
import { MonitoringFormTaskControllers } from '@app/interfaces/qm-module/qm-manage/qm-monioring-tasks.type';
import { useChangeMFTaskControllersMutation } from '@app/store/api/qm-module/qm-manage/qm-manage-monitoring-tasks.api';
import { useGetUsersListWithRolesQuery } from '@app/store/api/qm-module/qm-users/qm-users.api';
import { formFieldNameBaseStyles } from '@app/styles/base-styles';
import { Select, CircleButton, Button, Input, Box, PageHeader, Toggle } from '@ui';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useOutletContext } from 'react-router';

import { MonitoringTaskFormOutlet } from './monitoring-task-settings-layout';
const keyStorage = 'qm_module_option_MFTasks_load_distribution';
const MonitoringTasksControllersTab = () => {
  const { t } = useTranslation('pages.qmManage.monitoring_tasks.controllers_modal');
  const { mFTaskObject: taskData, thatTaskControllers } =
    useOutletContext<MonitoringTaskFormOutlet>();
  const [load_distribution, setLoadDistribution] = useState<
    MonitoringFormTaskControllers['settings']['controllers']['load_distribution']
  >((localStorage.getItem(keyStorage) as 'MANUAL') || 'MANUAL');
  const [editableIndex, setEditableIndex] = useState<number[]>([]);

  const { data: qmUsersList } = useGetUsersListWithRolesQuery({
    role_qm: 'controller',
    limit: 100,
    offset: 0,
  });

  const [updateControllers] = useChangeMFTaskControllersMutation();

  const [controllersList, setControllersList] = useState(
    thatTaskControllers?.settings?.controllers?.items,
  );
  const selectedContrIds = controllersList?.map(({ id }) => id);

  const optionsFromControllers: OptionItem[] =
    qmUsersList?.qm_users.map((item) => {
      return {
        title: item.username,
        value: item.id,
        nullable: selectedContrIds?.includes(item.id),
      };
    }) || [];

  useEffect(() => {
    setControllersList(thatTaskControllers?.settings?.controllers?.items);
  }, [thatTaskControllers?.settings?.controllers?.items]);

  const handleSave = useCallback(() => {
    const dataForField: MonitoringFormTaskControllers = {
      id: taskData?.id || '',
      settings: {
        controllers: {
          items:
            controllersList
              ?.filter((i) => !!i.id)
              .map((i) => (load_distribution === 'MANUAL' ? i : { id: i.id })) || [],
          load_distribution,
        },
      },
    };
    updateControllers({ body: dataForField, params: { id: taskData?.id || '' } });
  }, [controllersList, load_distribution, taskData?.id, updateControllers]);

  useEffect(() => {
    if (
      JSON.stringify(controllersList) ===
        JSON.stringify(thatTaskControllers?.settings?.controllers?.items) ||
      editableIndex.length > 0
    )
      return;
    handleSave();
  }, [
    controllersList,
    editableIndex.length,
    handleSave,
    thatTaskControllers?.settings?.controllers?.items,
  ]);

  const sumPercents = controllersList?.reduce((acc, cur) => (acc += cur.id_percent || 0), 0);

  function handleApproveController(index) {
    setEditableIndex((prev) => prev.filter((el) => el !== index));
  }
  function handleEditController(index) {
    setEditableIndex((prev) => [...prev, index]);
  }
  function handleRemoveElem(removeIndex: number) {
    setControllersList((prev) => prev?.filter((_, index) => index !== removeIndex));
  }

  const tableData = controllersList?.map(({ id, id_percent }, index) => {
    const editMode = editableIndex.includes(index);
    return {
      username: !editMode ? (
        qmUsersList?.qm_users.find((user) => user.id === id)?.username || id || ''
      ) : (
        <div className="max-w-[430px]">
          <Select
            defaultValue={id}
            options={optionsFromControllers}
            onChange={(val) => {
              setControllersList((prev) =>
                prev?.map((elem, elemIndex) => {
                  if (index === elemIndex) {
                    return { ...elem, id: val as string };
                  }
                  return elem;
                }),
              );
            }}
          />
        </div>
      ),
      id_percent: editMode ? (
        <Field
          key={index}
          id_percent={id_percent}
          percentsSum={sumPercents}
          index={index}
          setControllersList={setControllersList}
        />
      ) : (
        id_percent || '-'
      ),
      actions: (
        <div className="max-w-[80px] flex items-center gap-[5px]">
          <CircleButton
            hintTitle={t('hints.edit')}
            size={16}
            icon={editMode ? 'CheckIcon' : 'EditIcon'}
            disable={!id}
            className={classNames(
              `transition-all ${editMode ? 'text-basic_green' : 'text-action'}`,
            )}
            onClick={() =>
              editMode ? handleApproveController(index) : handleEditController(index)
            }
          />
          <CircleButton
            size={14}
            hintTitle={t('hints.delete')}
            icon="XIcon"
            onClick={() => handleRemoveElem(index)}
            className="text-basic_red"
          />
        </div>
      ),
    };
  });

  const tableColumns: TableColumn[] = [
    { title: t('table.username'), index: 'username' },
    { title: t('table.id_percent'), index: 'id_percent' },
    { title: t('table.actions'), index: 'actions', maxWidth: 110 },
  ];

  function handleAddControllerForm() {
    const emptyControllerData =
      load_distribution === 'MANUAL' ? { id: '', id_percent: 0 } : { id: '' };
    setControllersList((prev) => [...(prev || []), emptyControllerData]);
    setEditableIndex((prev) => [...(prev || []), controllersList?.length || 0]);
  }
  return (
    <>
      <PageHeader label="">
        <div className="w-full flex items-center justify-end">
          <Button
            onClick={handleAddControllerForm}
            icon="CirclePlusIcon"
            label={t('add_controller_button')}
          />
        </div>
      </PageHeader>
      <div className="h-full flex flex-col  gap-[10px]">
        <div className="flex items-center flex-row-reverse justify-end gap-[10px]">
          <div>
            <Toggle
              checked={load_distribution === 'AUTO'}
              onChecked={(toggle) => {
                const value = toggle ? 'AUTO' : 'MANUAL';
                setLoadDistribution(value);
                localStorage.setItem(keyStorage, value);
              }}
            />
            {/* <FormTabSwitcher
              classname="!bg-[#efefef]"
              valuesList={loadDistributionOptions}
              isActive={load_distribution}
              onChange={(value) => {
                setLoadDistribution(
                  value as MonitoringFormTaskControllers['settings']['controllers']['load_distribution'],
                );
                localStorage.setItem(keyStorage, value);
              }}
            /> */}
          </div>
          <span className={formFieldNameBaseStyles}>{t('load_distribution_label')}</span>
        </div>
        <Box className="p-4">
          <div className="bg- h-full w-full p-4 flex flex-col gap-[10px] rounded-defaultR ">
            <RubberTable
              truncate={false}
              columns={
                load_distribution === 'AUTO' ? [tableColumns[0], tableColumns[2]] : tableColumns
              }
              dataSource={tableData || []}
            />
          </div>
        </Box>
        {load_distribution === 'MANUAL' && (
          <div
            className={`text-[16px] flex w-full justify-end  ${
              sumPercents !== 100 ? 'text-basic_red' : 'text-3color'
            }`}
          >
            {t('total_distributed')}: {sumPercents} %
          </div>
        )}
      </div>
    </>
  );
};

export default MonitoringTasksControllersTab;

const Field = ({ id_percent, index, setControllersList, percentsSum }) => {
  const max_val = 100 - percentsSum + id_percent;
  return (
    <>
      <Input
        defaultValue={id_percent}
        type="number"
        max={max_val}
        error={id_percent > max_val ? 'Общий процент превышает значение - 100' : undefined}
        onChange={(e) => {
          const value = Number(e.target.value);
          setControllersList((prev) =>
            prev?.map((elem, elemIndex) => {
              if (index === elemIndex) {
                return { ...elem, id_percent: value };
              }
              return elem;
            }),
          );
        }}
      />
    </>
  );
};
